import { NgClass, NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { uniq } from "lodash";
import { ISigmaFile } from "projects/admin/src/app/+sigma/services/sigma.models";
import { SigmaService } from "projects/admin/src/app/+sigma/services/sigma.service";
import { LoaderComponent } from "../panel/loader/loader.component";
import { ISelectedSigmaObject, sigmaObjectKey } from "./select-sigma-object.models";

interface IFileNode {
	file: ISigmaFile;
	expanded: boolean;
	loading: boolean;
	children?: IFileNode[];
	pages?: IPageNode[];
}

interface IPageNode {
	page: ISigmaFile;
	elements: ISigmaFile[];
}

/** The levels of the Sigma file tree a consumer can allow the user to pick. */
export enum SigmaObjectLevel {
	Workbook = "workbook",
	Page = "page",
	Element = "element"
}

const CONTAINER_TYPES = new Set(["workspace", "folder"]);

/**
 * Renders the Sigma file tree and lets the user pick a workbook, page or element. Consumers decide
 * which levels are selectable and what to do with the selection, so this stays free of any
 * dashboard/widget or layout specific concerns.
 */
@Component({
	selector: "rl-select-sigma-object",
	templateUrl: "./select-sigma-object.component.html",
	styleUrls: ["./select-sigma-object.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [NgClass, NgTemplateOutlet, LoaderComponent]
})
export class SelectSigmaObjectComponent implements OnInit {
	private readonly _sigmaService = inject(SigmaService);


	rootNodes: IFileNode[] = [];
	loadingRoot = true;

	unselectableKeys: Set<string> = new Set<string>();
	selectedKeys: Set<string> = new Set<string>();

	/** Which levels of the tree render a select button */
	@Input()
	selectableLevels: SigmaObjectLevel[] = [SigmaObjectLevel.Workbook, SigmaObjectLevel.Page, SigmaObjectLevel.Element];

	/** The verb on the select buttons (ie. "Add workbook" vs "Select workbook") */
	@Input()
	actionLabel = "Add";

	/** Objects that are already in use and cannot be picked again */
	@Input()
	set unselectableObjects(objects: ISelectedSigmaObject[]) {
		this.unselectableKeys = this.buildKeys(objects);
	}

	/** Objects the user has picked so far, rendered as selected */
	@Input()
	set selectedObjects(objects: ISelectedSigmaObject[]) {
		this.selectedKeys = this.buildKeys(objects);
	}

	@Output()
	onObjectSelected = new EventEmitter<ISelectedSigmaObject>();

	// eslint-disable-next-line @typescript-eslint/naming-convention
	SigmaObjectLevel = SigmaObjectLevel;

	ngOnInit(): void {
		this._sigmaService.getFileTree().subscribe(files => {
			this.rootNodes = this.buildTree(files);
			this.loadingRoot = false;
		});
	}

	canSelect(level: SigmaObjectLevel) {
		return this.selectableLevels.includes(level);
	}

	toggleNode(node: IFileNode) {
		node.expanded = !node.expanded;
		if (!node.expanded) {
			return;
		}
		if (this.isWorkbook(node.file) && !node.pages && !node.loading) {
			node.loading = true;
			this._sigmaService.getWorkbookPagesAndElements(node.file.id).subscribe(entries => {
				const pages: IPageNode[] = [];
				const pagesById = new Map<string, IPageNode>();
				for (const entry of entries) {
					if (entry.type === "page") {
						const pageNode: IPageNode = { page: entry, elements: [] };
						pages.push(pageNode);
						pagesById.set(entry.id, pageNode);
					} else if (entry.type === "element" && entry.parentId && entry.name?.trim()) {
						const parent = pagesById.get(entry.parentId);
						if (parent) {
							parent.elements.push(entry);
						}
					}
				}
				node.pages = pages;
				node.loading = false;
			});
		}
	}

	isContainer(file: ISigmaFile): boolean {
		return CONTAINER_TYPES.has(file.type);
	}

	isWorkbook(file: ISigmaFile): boolean {
		return file.type === "workbook";
	}

	iconClass(file: ISigmaFile): string {
		if (file.type === "workspace") {
			return "fa-building";
		}
		if (file.type === "folder") {
			return "fa-folder";
		}
		if (file.type === "workbook") {
			return "fa-file-chart-line";
		}
		return "fa-file";
	}

	keyFor(workbookId: string, pageId?: string, elementId?: string) {
		return sigmaObjectKey(workbookId, pageId, elementId);
	}

	isDisabled(workbookId: string, pageId?: string, elementId?: string) {
		return this.unselectableKeys.has(this.keyFor(workbookId, pageId, elementId));
	}

	isSelected(workbookId: string, pageId?: string, elementId?: string) {
		return this.selectedKeys.has(this.keyFor(workbookId, pageId, elementId));
	}

	workbookSelected(workbook: ISigmaFile) {
		if (this.isDisabled(workbook.id)) {
			return;
		}
		this.onObjectSelected.emit({
			workbookId: workbook.id,
			workbookUrl: workbook.url,
			name: workbook.name
		});
	}

	pageSelected(workbook: ISigmaFile, page: ISigmaFile) {
		if (this.isDisabled(workbook.id, page.id)) {
			return;
		}
		this.onObjectSelected.emit({
			workbookId: workbook.id,
			workbookUrl: workbook.url,
			pageId: page.id,
			name: page.name
		});
	}

	elementSelected(workbook: ISigmaFile, page: ISigmaFile, element: ISigmaFile) {
		if (this.isDisabled(workbook.id, page.id, element.id)) {
			return;
		}
		this.onObjectSelected.emit({
			workbookId: workbook.id,
			workbookUrl: workbook.url,
			pageId: page.id,
			elementId: element.id,
			name: element.name
		});
	}

	trackByNodeId(_: number, node: IFileNode) {
		return node.file.id;
	}

	trackByPageId(_: number, node: IPageNode) {
		return node.page.id;
	}

	trackByFileId(_: number, file: ISigmaFile) {
		return file.id;
	}

	private buildKeys(objects: ISelectedSigmaObject[]): Set<string> {
		if (!objects) {
			return new Set<string>();
		}
		const keys = objects
			.filter(x => !!x?.workbookId)
			.map(x => sigmaObjectKey(x.workbookId, x.pageId, x.elementId));
		return new Set<string>(uniq(keys));
	}

	private buildTree(files: ISigmaFile[]): IFileNode[] {
		const relevant = files.filter(f => this.isContainer(f) || this.isWorkbook(f));
		const nodesById = new Map<string, IFileNode>();
		for (const file of relevant) {
			nodesById.set(file.id, {
				file: file,
				expanded: false,
				loading: false,
				children: this.isContainer(file) ? [] : undefined
			});
		}

		const roots: IFileNode[] = [];
		for (const node of nodesById.values()) {
			const parentId = node.file.parentId;
			const parent = parentId && parentId !== node.file.id ? nodesById.get(parentId) : undefined;
			if (parent?.children) {
				parent.children.push(node);
			} else {
				roots.push(node);
			}
		}

		return roots;
	}

}
